/**
 * Holdings Detail Modal Component
 * Shows detailed breakdown of holdings for a specific stock/MF
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Eye, RefreshCw } from 'lucide-react';
import { formatValue } from './utils';

const HoldingsDetailModal = ({
  isOpen,
  onClose,
  company,
  holdings,
  loading,
  activeTab
}) => {
  if (!company) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="holdings-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Eye className="text-primary" size={24} />
            Holdings Detail: {company?.display_name || company?.symbol}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin mr-2" size={24} />
            <span>Loading holdings...</span>
          </div>
        ) : holdings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No holdings found for this {activeTab === 'stocks' ? 'stock' : 'mutual fund'}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-xs text-gray-500 uppercase">Total Holdings</p>
                <p className="text-lg font-bold">{holdings.length}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Total {activeTab === 'stocks' ? 'Shares' : 'Units'}</p>
                <p className="text-lg font-bold">{holdings.reduce((s, h) => s + h.quantity, 0).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Current Value</p>
                <p className="text-lg font-bold text-primary">{formatValue(holdings.reduce((s, h) => s + h.current_value, 0))}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Total Gain</p>
                <p className={`text-lg font-bold ${holdings.reduce((s, h) => s + h.gain, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatValue(holdings.reduce((s, h) => s + h.gain, 0))}
                </p>
              </div>
            </div>

            {/* Holdings Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="py-2 px-3 font-semibold">Member</th>
                    <th className="py-2 px-3 font-semibold text-right">Qty</th>
                    <th className="py-2 px-3 font-semibold text-right">Avg Price</th>
                    <th className="py-2 px-3 font-semibold text-right">Current</th>
                    <th className="py-2 px-3 font-semibold text-right">Value</th>
                    <th className="py-2 px-3 font-semibold text-right">Gain</th>
                    <th className="py-2 px-3 font-semibold">Quality</th>
                    <th className="py-2 px-3 font-semibold">Status</th>
                    <th className="py-2 px-3 font-semibold">Account</th>
                  </tr>
                </thead>
                <tbody>
                  {holdings.map((holding, idx) => (
                    <tr key={holding.id || idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="py-2 px-3">
                        <div>
                          <p className="font-medium">{holding.member_name}</p>
                          <p className="text-xs text-gray-500">{holding.member_relation}</p>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        {holding.quantity.toLocaleString('en-IN')}
                      </td>
                      <td className="py-2 px-3 text-right">
                        ₹{holding.purchase_price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3 text-right">
                        ₹{holding.current_price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        {formatValue(holding.current_value)}
                      </td>
                      <td className={`py-2 px-3 text-right font-medium ${holding.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        <div>
                          <p>{formatValue(holding.gain)}</p>
                          <p className="text-xs">({holding.gain_percent >= 0 ? '+' : ''}{holding.gain_percent.toFixed(1)}%)</p>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {holding.quality_tag ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            holding.quality_tag === 'High' ? 'bg-green-100 text-green-800' :
                            holding.quality_tag === 'Moderate' ? 'bg-blue-100 text-blue-800' :
                            holding.quality_tag === 'Low' ? 'bg-yellow-100 text-yellow-800' :
                            holding.quality_tag === 'V Low' ? 'bg-orange-100 text-orange-800' :
                            holding.quality_tag === 'VV Low' ? 'bg-red-100 text-red-800' :
                            holding.quality_tag === 'New' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {holding.quality_tag}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          holding.status === 'Pledged' ? 'bg-amber-100 text-amber-800' :
                          holding.status === 'Unlisted' ? 'bg-gray-100 text-gray-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {holding.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {holding.las_account_name && (
                          <p className="text-amber-700">LAS: {holding.las_account_name}</p>
                        )}
                        {holding.mtf_account_name && (
                          <p className="text-purple-700">MTF: {holding.mtf_account_name}</p>
                        )}
                        {holding.broker && (
                          <p className="text-gray-600">{holding.broker}</p>
                        )}
                        {holding.demat_account && (
                          <p className="text-gray-500">{holding.demat_account}</p>
                        )}
                        {!holding.las_account_name && !holding.mtf_account_name && !holding.broker && !holding.demat_account && (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-200 font-semibold">
                    <td className="py-2 px-3">TOTAL</td>
                    <td className="py-2 px-3 text-right">{holdings.reduce((s, h) => s + h.quantity, 0).toLocaleString('en-IN')}</td>
                    <td className="py-2 px-3 text-right">-</td>
                    <td className="py-2 px-3 text-right">-</td>
                    <td className="py-2 px-3 text-right">{formatValue(holdings.reduce((s, h) => s + h.current_value, 0))}</td>
                    <td className={`py-2 px-3 text-right ${holdings.reduce((s, h) => s + h.gain, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatValue(holdings.reduce((s, h) => s + h.gain, 0))}
                    </td>
                    <td className="py-2 px-3">-</td>
                    <td className="py-2 px-3">-</td>
                    <td className="py-2 px-3">-</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HoldingsDetailModal;
